import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, GraduationCap, Sparkles } from "lucide-react";
import { authFetch } from "../lib/auth";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NEW_BADGE_DAYS = 14;
const SEEN_KEY_PREFIX = "mindforge_video_seen_";

function isNew(createdAt) {
  if (!createdAt) return false;
  const ageMs = Date.now() - new Date(createdAt).getTime();
  return ageMs < NEW_BADGE_DAYS * 24 * 60 * 60 * 1000;
}

function markSeen(videoId) {
  try {
    localStorage.setItem(SEEN_KEY_PREFIX + videoId, "1");
  } catch (e) {
    /* ignore */
  }
}

function hasSeen(videoId) {
  try {
    return localStorage.getItem(SEEN_KEY_PREFIX + videoId) === "1";
  } catch (e) {
    return false;
  }
}

const SECTION_LABEL = {
  numbers: "Numbers",
  words: "Words",
  names: "Names",
  working_memory: "Working Memory",
};

const VideoCard = ({ video, onPlay, isFeatured }) => {
  const showNewBadge = isNew(video.created_at) && !hasSeen(video.video_id);
  const [thumbFailed, setThumbFailed] = useState(false);
  const showThumb = video.thumbnail_url && !thumbFailed;
  return (
    <motion.button
      whileHover={{ y: -2 }}
      onClick={() => onPlay(video)}
      data-testid={`video-card-${video.video_id}`}
      className={`group relative text-left flex-shrink-0 ${
        isFeatured ? "w-full" : "w-64"
      }`}
    >
      <div className="relative overflow-hidden rounded-md bg-muted aspect-video">
        {showThumb ? (
          <img
            src={video.thumbnail_url}
            alt={video.title}
            onError={() => setThumbFailed(true)}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full bg-gradient-to-br from-primary/25 via-card to-card flex flex-col items-center justify-center gap-2 px-4 text-center"
            data-testid={`video-placeholder-${video.video_id}`}
          >
            <GraduationCap className="w-8 h-8 text-primary/70" />
            <span className="text-sm font-medium text-foreground line-clamp-2">
              {video.title}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-primary/95 flex items-center justify-center shadow-lg">
            <Play className="w-6 h-6 text-primary-foreground ml-1" fill="currentColor" />
          </div>
        </div>
        {showNewBadge && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold tracking-widest uppercase rounded-sm">
            NEW
          </div>
        )}
      </div>
      <div className={isFeatured ? "p-4" : "pt-2"}>
        <h4
          className={`font-medium text-foreground line-clamp-2 ${
            isFeatured ? "text-lg" : "text-sm"
          }`}
        >
          {video.title}
        </h4>
        {isFeatured && video.description && (
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {video.description}
          </p>
        )}
      </div>
    </motion.button>
  );
};

const VideoModal = ({ video, onClose }) => {
  // Lock body scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!video) return null;
  const autoplaySrc =
    video.embed_url +
    (video.embed_url.includes("?") ? "&" : "?") +
    "autoplay=1";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      data-testid="video-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="video-modal-close-btn"
          className="absolute -top-12 right-0 text-white/80 hover:text-white flex items-center gap-2 text-sm"
        >
          Close <X className="w-4 h-4" />
        </button>
        <div className="aspect-video w-full bg-black rounded-md overflow-hidden">
          <iframe
            src={autoplaySrc}
            title={video.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full border-0"
          />
        </div>
        {(video.title || video.description) && (
          <div className="mt-4 text-white">
            <h3 className="font-heading text-xl font-semibold">{video.title}</h3>
            {video.description && (
              <p className="text-sm text-white/70 mt-1">{video.description}</p>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const TutorialVideoSection = ({ section }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/videos?section=${section}`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setVideos(data.videos || []);
        }
      } catch (e) {
        // silent — videos are optional
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section]);

  const handlePlay = (video) => {
    markSeen(video.video_id);
    setActive(video);
  };

  if (loading || videos.length === 0) return null;

  const featured = videos[0];
  const others = videos.slice(1);

  return (
    <>
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mt-10"
        data-testid="tutorial-video-section"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold">
              How to train {SECTION_LABEL[section] || section}
            </h3>
            <p className="text-xs text-muted-foreground">
              Tutorials &amp; tips from Memory School
            </p>
          </div>
        </div>

        <Card className="bg-card border-border overflow-hidden">
          <CardContent className="p-4 md:p-6">
            <VideoCard video={featured} onPlay={handlePlay} isFeatured />

            {others.length > 0 && (
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                  More tutorials
                </p>
                <div
                  className="flex gap-4 overflow-x-auto pb-2 -mx-4 md:-mx-6 px-4 md:px-6 snap-x snap-mandatory"
                  data-testid="tutorial-more-videos"
                >
                  {others.map((v) => (
                    <div key={v.video_id} className="snap-start">
                      <VideoCard video={v} onPlay={handlePlay} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.section>

      <AnimatePresence>
        {active && <VideoModal video={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </>
  );
};

export default TutorialVideoSection;
